import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 } from "uuid";
import ConnectionData from "../../../js/connection/ConnectionData";
import {
    CanvasPath,
    getConvexHull,
    isPointInsideConvexHull,
    Point
} from "../../../js/drawing/CanvasPath";
import CanvasDrawing from "../../../js/drawing/CanvasDrawing";
import { DrawingMode } from "./DrawingControls";

type Props = {
    drawingMode: DrawingMode;
    canvasDrawing: CanvasDrawing;
    connectionData: ConnectionData;
};

export default function DrawingOverlay({
    drawingMode,
    canvasDrawing,
    connectionData
}: Props) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const contextRef = useRef<CanvasRenderingContext2D | null>(null);
    const [paths, setPaths] = useState<Record<string, CanvasPath>>({});

    const lastTimestamp = useMemo(
        () =>
            Object.values(paths)
                .map((p) => p.timestamp)
                .sort((a, b) => a - b)
                .at(-1) || 0,
        [paths]
    );

    const [isDrawing, setIsDrawing] = useState(false);
    const [currentPoints, setCurrentPoints] = useState<Point[]>([]);

    useEffect(() => {
        contextRef.current = canvasRef.current!.getContext("2d");
    }, []);

    useEffect(() => {
        if (canvasDrawing.myConnectionData!.id === connectionData.id) {
            return;
        }

        const interval = window.setInterval(() => {
            canvasDrawing.sendSyncPing(connectionData.id, lastTimestamp);
        }, 1000);

        return () => window.clearInterval(interval);
    }, [connectionData.id, canvasDrawing, lastTimestamp]);

    const getCanvasResolutionAndOffset = useCallback(() => {
        const rect = canvasRef.current!.getBoundingClientRect();

        const videoAspect =
            connectionData.videoWidth / connectionData.videoHeight;
        const containerAspect = rect.width / rect.height;

        let displayWidth, displayHeight, offsetX, offsetY;

        if (containerAspect > videoAspect) {
            // container is wider than video -> crop top/bottom
            displayWidth = rect.width;
            displayHeight = rect.width / videoAspect;
            offsetX = 0;
            offsetY = (rect.height - displayHeight) / 2;
        } else {
            // container is taller than video -> crop sides
            displayWidth = rect.height * videoAspect;
            displayHeight = rect.height;
            offsetX = (rect.width - displayWidth) / 2;
            offsetY = 0;
        }

        return { offsetX, offsetY, displayWidth, displayHeight };
    }, [connectionData.videoWidth, connectionData.videoHeight]);

    const videoPointToCanvas = useCallback(
        (point: Point) => {
            const videoX = point.x * connectionData.videoWidth;
            const videoY = point.y * connectionData.videoHeight;

            const { offsetX, offsetY, displayWidth, displayHeight } =
                getCanvasResolutionAndOffset();

            const canvasX =
                (videoX * displayWidth) / connectionData.videoWidth + offsetX;
            const canvasY =
                (videoY * displayHeight) / connectionData.videoHeight + offsetY;
            return {
                x: canvasX,
                y: canvasY
            };
        },
        [connectionData, getCanvasResolutionAndOffset]
    );

    const drawPath = useCallback(
        (path: CanvasPath) => {
            const context = contextRef.current!;
            context.strokeStyle = path.color;
            context.lineCap = "round";
            context.lineWidth = 5;

            for (const point of path.points) {
                const canvasPoint = videoPointToCanvas(point);

                context.lineTo(canvasPoint.x, canvasPoint.y);
                context.stroke();
                context.beginPath();
                context.moveTo(canvasPoint.x, canvasPoint.y);
            }
            context.beginPath();
        },
        [videoPointToCanvas]
    );

    const onCanvasPath = useCallback(
        (event: any) => {
            const { path } = event.detail;
            if (path.toConnectionId === connectionData.id) {
                drawPath(path);

                setPaths((prev) => ({ ...prev, [path.id]: path }));
            }
        },
        [connectionData.id, drawPath]
    );

    const clearAndRedraw = useCallback(() => {
        if (canvasRef.current) {
            const canvas = canvasRef.current;

            canvas.width = canvas.clientWidth;
            canvas.height = canvas.clientHeight;

            for (const path of Object.values(paths)) {
                drawPath(path);
            }
        }
    }, [drawPath, paths]);

    const onPathErased = useCallback(
        (event: any) => {
            const { path } = event.detail;
            if (path.toConnectionId === connectionData.id) {
                setPaths((prev) => {
                    const updatedPaths = { ...prev };
                    delete updatedPaths[path.id];
                    return updatedPaths;
                });
                clearAndRedraw();
            }
        },
        [connectionData.id, clearAndRedraw]
    );

    const onSyncPing = useCallback(
        (event: any) => {
            const { id, timestamp } = event.detail;

            if (id !== connectionData.id) {
                return;
            }

            if (timestamp < lastTimestamp) {
                canvasDrawing.sendAllCanvasPaths(Object.values(paths), id);
            }
        },
        [connectionData.id, lastTimestamp, canvasDrawing, paths]
    );

    const onSync = useCallback(
        (event: any) => {
            const {
                paths,
                toConnectionId
            }: { paths: CanvasPath[]; toConnectionId: string } = event.detail;

            if (toConnectionId === connectionData.id) {
                setPaths(
                    paths.reduce(
                        (result, curr) => ({ ...result, [curr.id]: curr }),
                        {}
                    )
                );
                clearAndRedraw();
            }
        },
        [clearAndRedraw, connectionData.id]
    );

    useEffect(() => {
        const resizeObserver = new ResizeObserver(clearAndRedraw);
        resizeObserver.observe(connectionData.view);

        canvasDrawing.events.addEventListener("oncanvaspath", onCanvasPath);
        canvasDrawing.events.addEventListener("onpatherased", onPathErased);
        canvasDrawing.events.addEventListener("onsyncping", onSyncPing);
        canvasDrawing.events.addEventListener("onsync", onSync);

        return () => {
            canvasDrawing.events.removeEventListener(
                "oncanvaspath",
                onCanvasPath
            );
            canvasDrawing.events.removeEventListener(
                "onpatherased",
                onPathErased
            );
            canvasDrawing.events.removeEventListener("onsyncping", onSyncPing);
            canvasDrawing.events.removeEventListener("onsync", onSync);

            resizeObserver.disconnect();
        };
    }, [
        canvasDrawing.events,
        connectionData.view,
        onCanvasPath,
        clearAndRedraw,
        onPathErased,
        onSyncPing,
        onSync
    ]);

    function normalizePointToVideo(point: Point): Point {
        const rect = canvasRef.current!.getBoundingClientRect();
        const clickX = point.x - rect.left;
        const clickY = point.y - rect.top;

        const { offsetX, offsetY, displayWidth, displayHeight } =
            getCanvasResolutionAndOffset();

        // Remove offset and scale to original video dimensions
        const videoX =
            (clickX - offsetX) * (connectionData.videoWidth / displayWidth);
        const videoY =
            (clickY - offsetY) * (connectionData.videoHeight / displayHeight);

        return {
            x: videoX / connectionData.videoWidth,
            y: videoY / connectionData.videoHeight
        };
    }

    function draw(point: Point) {
        if (!isDrawing) {
            return;
        }

        if (drawingMode === "eraser") {
            erase(point);
            return;
        }

        const videoPoint = normalizePointToVideo(point);
        setCurrentPoints((prev) => [...prev, videoPoint]);

        const context = contextRef.current!;
        const rect = canvasRef.current!.getBoundingClientRect();
        const x = point.x - rect.left;
        const y = point.y - rect.top;

        context.strokeStyle = canvasDrawing.myConnectionData!.color;
        context.lineCap = "round";
        context.lineWidth = 5;
        context.lineTo(x, y);
        context.stroke();
        context.beginPath();
        context.moveTo(x, y);
    }

    function erase(point: Point) {
        const videoPoint = normalizePointToVideo(point);

        const bboxHit = Object.values(paths).find((p) =>
            isPointInsideConvexHull(videoPoint, p.convexHull)
        );

        if (bboxHit && paths[bboxHit.id]) {
            canvasDrawing.eraseCanvasPath({
                id: bboxHit.id,
                toConnectionId: connectionData.id
            });

            setPaths((prev) => {
                const updatedPaths = { ...prev };
                delete updatedPaths[bboxHit.id];
                return updatedPaths;
            });
            clearAndRedraw();
        }
    }

    function startDrawing(point: Point) {
        if (drawingMode === "none") {
            return;
        }

        setIsDrawing(true);
        setCurrentPoints([]);

        if (drawingMode === "eraser") {
            erase(point);
        }
    }

    function stopDrawing() {
        if (!isDrawing) {
            return;
        }

        setIsDrawing(false);

        if (drawingMode === "eraser") {
            return;
        }

        // Reset the path
        contextRef.current!.beginPath();

        if (currentPoints.length > 0) {
            const path: CanvasPath = {
                id: v4(),
                points: currentPoints,
                color: canvasDrawing.myConnectionData!.color,
                toConnectionId: connectionData.id,
                convexHull: getConvexHull(currentPoints),
                timestamp: Date.now()
            };

            setPaths((prev) => ({ ...prev, [path.id]: path }));

            canvasDrawing.sendCanvasPath(path);
        }
    }

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%"
            }}
            onMouseDown={(e) => startDrawing({ x: e.clientX, y: e.clientY })}
            onMouseMove={(e) => draw({ x: e.clientX, y: e.clientY })}
            onMouseUp={stopDrawing}
            onTouchStart={(e) =>
                startDrawing({
                    x: e.touches[0].clientX,
                    y: e.touches[0].clientY
                })
            }
            onTouchMove={(e) =>
                draw({ x: e.touches[0].clientX, y: e.touches[0].clientY })
            }
            onTouchEnd={stopDrawing}
        />
    );
}
